import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useState } from "react";
import { TIERS } from "constants/pricing";
import { GenericConfigContext } from "/contexts/generic-config-context";
import productPricingConfig from "segments/desktop/pricing/product-pricing-config";
import classnames from "classnames";
import DownArrow from "/static/svg/arrow-down.svg";
import Button from "components/core/button/button-component";
import SignupFormComponent from "components/core/signups/base-signup-form/signup-form-component";
import { withAbTests } from "/contexts/ab-tests-context";
import { getPricingContactUsLink } from "services/pricing/pricing-page/contact-us-link-service";
import { getTierTranslationKey } from "services/pricing/plans-service";
import { ProductIcon } from "segments/desktop/pricing/icons/product-icon";
import { trackEvent } from "services/bigbrain-service";
import { HOMEPAGE_PRICING_TIER_SOURCE } from "constants/cookies";
import { SEE_COMPLETE_FEATURES_LIST_CLICKED, CONTACT_US_BUTTON_CLICKED } from "constants/bigbrain-event-types";
import { ProductFeaturesTableSection } from "./product-features-table-section/product-features-table-section";
import { setCookie } from "/services/cookies-service";
import { StyledProductFeaturesTable } from "./product-features-table.scss";
const ProductFeaturesTable = ({ productName: currentProductName, isExpanded: isExpandedProp, setIsExpanded: setIsExpandedProp, abTests, hideFreeTier, hasFreeTier, isNewPricing, shouldHideBasicTier })=>{
    const currentProduct = productPricingConfig[currentProductName];
    const { translate } = useContext(GenericConfigContext);
    const [localIsExpanded, setLocalIsExpanded] = useState(false);
    const controlledMode = setIsExpandedProp !== undefined;
    const setIsExpanded = controlledMode ? setIsExpandedProp : setLocalIsExpanded;
    const isExpanded = controlledMode ? isExpandedProp : localIsExpanded;
    const toggleFeaturesList = useCallback(()=>{
        const newToggleValue = !isExpanded;
        setIsExpanded(newToggleValue);
        trackEvent(SEE_COMPLETE_FEATURES_LIST_CLICKED, {
            info1: newToggleValue ? "shown" : "hidden",
            productKind: currentProductName,
            button_name: "dropdown_features_list_pricing_page"
        });
    }, [
        setIsExpanded,
        isExpanded
    ]);
    const isFeatureListEmpty = useCallback(()=>{
        const product = productPricingConfig[currentProductName];
        if (!product || !product.featuresList) {
            return true;
        }
        return product.featuresList.length === 0;
    }, []);
    const getTiers = useCallback(()=>{
        if (!hasFreeTier || hideFreeTier) {
            return currentProduct.tiers.filter((tier)=>tier.name !== TIERS.FREE);
        }
        return currentProduct.tiers;
    }, [
        currentProduct,
        hasFreeTier,
        hideFreeTier
    ]);
    const handleBeforeSubmitCallback = (tierName)=>{
        setCookie(HOMEPAGE_PRICING_TIER_SOURCE, `${currentProductName}-${tierName}`);
    };
    const productName = translate(`pricingPage.products.${currentProductName}.longTitleBold`);
    const featuresList = isNewPricing ? currentProduct.newFeaturesList : currentProduct.featuresList;
    const filteredTiers = shouldHideBasicTier ? getTiers().filter((tier)=>tier.name !== TIERS.BASIC) : getTiers();
    return /*#__PURE__*/ _jsxs(StyledProductFeaturesTable, {
        className: "product-features-table",
        isNewPricing: isNewPricing,
        numberOfTiers: filteredTiers.length,
        role: "table",
        style: {
            "--primary-color": currentProduct.primaryColor,
            "--secondary-color": currentProduct.secondaryColor,
            "--logo-color": currentProduct.switcherIconColor
        },
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: classnames("toggle", {
                    expanded: isExpanded
                }),
                onClick: ()=>toggleFeaturesList(),
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        children: translate("pricingPage.completeFeaturesList")
                    }),
                    /*#__PURE__*/ _jsx(DownArrow, {
                        className: "arrow-down"
                    })
                ]
            }),
            isExpanded && /*#__PURE__*/ _jsxs("div", {
                className: "grid",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "tier-header"
                    }),
                    filteredTiers.map((tier)=>/*#__PURE__*/ _jsxs("div", {
                            className: "tier-header",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "tier-title",
                                    children: translate(getTierTranslationKey(tier.name, {
                                        hasStudentPlan: false
                                    }))
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "tier-cta",
                                    children: tier.name !== TIERS.ENTERPRISE ? /*#__PURE__*/ _jsx(SignupFormComponent, {
                                        buttonSize: "xs",
                                        extraData: {
                                            pricing_tier: tier.name,
                                            cta_name: "full_feature_list",
                                            productKind: currentProductName
                                        },
                                        beforeSubmitCallback: ()=>handleBeforeSubmitCallback(tier === null || tier === void 0 ? void 0 : tier.name),
                                        useCtaText: isNewPricing,
                                        ctaText: isNewPricing ? translate("signup.tryForFree") : null,
                                        className: isNewPricing ? "tier-cta-button" : undefined
                                    }) : /*#__PURE__*/ _jsx(Button, {
                                        type: "button",
                                        buttonSize: "xs",
                                        onClickCallback: ()=>{
                                            trackEvent(CONTACT_US_BUTTON_CLICKED, {
                                                info1: "enterprise",
                                                placement: "feature_list"
                                            });
                                            window.location.href = getPricingContactUsLink(abTests);
                                        },
                                        children: translate("footer.contactSales")
                                    })
                                })
                            ]
                        }, `tier-header-${tier.name}`)),
                    !isFeatureListEmpty() && /*#__PURE__*/ _jsxs("div", {
                        className: "product-section-header",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "icon-container",
                                children: /*#__PURE__*/ _jsx(ProductIcon, {
                                    product: `${currentProductName}White`,
                                    className: "product-section-logo"
                                })
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "product-name",
                                dangerouslySetInnerHTML: {
                                    __html: productName
                                }
                            })
                        ]
                    }),
                    !isFeatureListEmpty() && (featuresList === null || featuresList === void 0 ? void 0 : featuresList.map((productSection)=>/*#__PURE__*/ _jsx(ProductFeaturesTableSection, {
                            productSection: productSection,
                            productName: currentProductName,
                            hideFreeTier: hideFreeTier,
                            hasFreeTier: hasFreeTier,
                            isNewPricing: isNewPricing,
                            shouldHideBasicTier: shouldHideBasicTier
                        }, productSection.sectionName)))
                ]
            })
        ]
    });
};
export default withAbTests(ProductFeaturesTable);
